import React, { useContext, useState, useCallback, useMemo } from 'react';
import { ModuleType } from 'infrastructure/enums';

interface IContextProps {
  module: ModuleType;
  changeModule: (module: ModuleType) => void;
}

const ModuleContext = React.createContext<IContextProps | undefined>(undefined);

export const useModuleWithLocalApcm = (defaultModule = ModuleType.RPM) => {
  const { module: globalModule, changeModule } = useModule(defaultModule);
  const [isLocalApcm, setIsLocalApcm] = React.useState(false);

  const module = useMemo(
    () => (isLocalApcm ? ModuleType.APCM : globalModule),
    [isLocalApcm, globalModule],
  );

  const change = useCallback(
    (m: ModuleType) => {
      if (m === ModuleType.APCM) {
        setIsLocalApcm(true);
      } else {
        setIsLocalApcm(false);
        changeModule(m);
      }
    },
    [changeModule],
  );

  return { module, changeModule: change };
};

export const useModule = (defaultModule = ModuleType.RPM, withApcm = false) => {
  const context = useContext(ModuleContext);
  if (!context) {
    const [module, setModule] = useState(defaultModule);
    return {
      module,
      changeModule: (m: ModuleType) =>
        setModule((prev) => {
          if (withApcm) {
            return m === ModuleType.APCM ? prev : m;
          }
          return m;
        }),
    };
  }

  return context;
};

interface IProps {
  children?: React.ReactNode;
}

const ModuleProvider: React.FC<IProps> = ({ children }) => {
  const [module, setModule] = useState(ModuleType.RPM);

  return (
    <ModuleContext.Provider
      value={{
        module,
        changeModule: setModule,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
};

export default ModuleProvider;
